import { FlightPassengerHighlight } from 'components/flighteditor/unified/passenger-guidance';
import {ColumnProps} from "antd/lib/table";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBoxOpen} from "@fortawesome/free-solid-svg-icons";
import {Icon, Tooltip, Typography} from "antd";
import React from "react";
import {NotesForPilotQuickEdit} from "./notes-for-pilot/quickedit/notes-for-pilot-quick-edit";
import {FlightLeg} from "../../../schema";
import moment from "moment-timezone";
import PaxNameCell from "../../../common/person/pax-name-cell";
import SafetyCardStatus from "../../scheduling/safety-card-status";
import {FlightEditorEntityColumn} from "../../flighteditor/unified/configuration";

type TEMP_TYPE_Pax = any;
type TEMP_TYPE_Cgo = any;
type TEMP_TYPE_Entity = TEMP_TYPE_Pax | TEMP_TYPE_Cgo

export function createScheduledEntityColumns(leg: FlightLeg=null, options?: {
    containerRef?: HTMLElement,
    reviewMode?: boolean,
    showSafetyCard?: boolean,
    showNotesForPilot?: boolean,
    columns?: FlightEditorEntityColumn[]
}){

    const { containerRef, reviewMode, showSafetyCard, showNotesForPilot = true, columns: visibleColumns } = options || {};

    const columns: ColumnProps<TEMP_TYPE_Entity>[] = [
        {
            title: 'Scheduled Group',
            key: 'scheduledGroup',
            dataIndex: 'scheduledGroup'
        },
        {
            title: 'Name',
            key: 'name',
            render: (_, record: TEMP_TYPE_Entity) => {
                let iconStyle = {
                    marginRight: 6
                }

                let icons = [];

                const name = record.__typename === 'CargoNode' ? 'CGO' : 'PAX';
                let paxDep = record.departureID?._id;
                let paxDest = record.destinationID?._id;
                // if (record.transitType === 'INBOUND'){
                //     let tmp = paxDest;
                //     paxDest = paxDep;
                //     paxDep = tmp;
                // }

                if (leg && paxDep === leg.departureID){
                    icons.push(<Tooltip title={name + ' gets on this leg.'}>
                        <Icon type="arrow-up" style={{ color: 'lime' }} />
                    </Tooltip>)
                }

                if (leg && paxDest === leg.destinationID){
                    icons.push(<Tooltip title={name + ' gets off this leg.'}>
                        <Icon type="arrow-down" style={{ color: 'red' }} />
                    </Tooltip>)
                }

                let text;

                if(record.__typename === 'CargoNode'){
                    text = <span><FontAwesomeIcon icon={faBoxOpen} style={iconStyle} /> {record.name}</span>
                }
                else {
                    text = <span><Icon type="user" style={iconStyle}/>
                        <FlightPassengerHighlight passengerID={record._id}>
                            <PaxNameCell
                                record={record}
                                showDeletedPersonNotice
                                deletedPersonNoticeTooltip={
                                    "This passenger's associated person record has been deleted. Please remove. Person will not show up in printout."
                                }
                            />
                        </FlightPassengerHighlight>
                    </span>
                }
                return <>
                    {icons.map((el, index) => <span style={{ marginRight: index + 1 === icons.length ? '6px' : '0' }}>{el}</span>)}
                    {text}
                </>
            }
        },
        {
            title: 'Employer',
            key: 'employer',
            render: (_, record: TEMP_TYPE_Entity) => {
                if (record.classType === 'flytsuite.paxnode'){
                    return record.employerID ? record.employerID.name : <span style={{color: 'red'}}>Not Found</span>
                }
                else if (record.classType === 'flytsuite.cgonode'){
                    return <Typography.Text type="secondary">N/A</Typography.Text>
                }
                return null
            }
        },
        {
            title: 'Controlling Heliport',
            key: 'lastKnownController',
            dataIndex: 'lastKnownController',
            render: (doc) => doc?.name
        },
        {
            title: 'Weight',
            key: 'weight',
            render: (_, record: TEMP_TYPE_Entity) => {
                if(record.__typename === 'CargoNode'){
                    return `${record.weight}`
                }
                return `${record.paxWeight}`
            }
        },
        {
            title: 'Bag Weight',
            key: 'bagWeight',
            dataIndex: 'bagWeight'
        },
        {
            title: 'Bag Count',
            key: 'bagCount',
            dataIndex: 'bagCount'
        },
        {
            title: 'Departure',
            key: 'departure',
            render: (_, record: TEMP_TYPE_Entity) => record.departureID ? record.departureID.name : <span style={{color: 'red'}}>Not Found</span>
        },
        {
            title: 'Destination',
            key: 'destination',
            render: (_, record: TEMP_TYPE_Entity) => record.destinationID ? record.destinationID.name : <span style={{color: 'red'}}>Not Found</span>
        },
        {
            title: 'Transit Type',
            key: 'transitType',
            dataIndex: 'transitType'
        },
        {
            title: 'Pilot Notes',
            key: 'pilotNotes',
            render: (_, row: TEMP_TYPE_Entity) => {
                return <NotesForPilotQuickEdit
                    scheduleNodeData={row}
                    readOnly
                    disabled={reviewMode}
                    popupContainer={containerRef}
                />
            }
        },
        {
            title: 'Safety Card Expiration',
            key: 'safetyCard',
            width: 175,
            render: (_, record: TEMP_TYPE_Entity) => {
                if (record.classType !== 'flytsuite.paxnode') {
                    return <Typography.Text type="secondary">N/A</Typography.Text>;
                }

                return <SafetyCardStatus credentials={record.personID?.credentials} />
            }
        },
        {
            title: 'Requested Check-in Time',
            key: 'requestedCheckInTime',
            dataIndex: 'requestedCheckInTime',
            width: 150,
            render: (checkInTime, row) => {

                const checkInMoment = checkInTime ? moment.tz(checkInTime, 'HH:mm:ss', 'America/Chicago') : null;

                if (!checkInMoment) return '—';

                return <span style={{ whiteSpace: 'nowrap' }}>{checkInMoment.format('HH:mm') + ' CST'}</span>
            }
        },
        {
            title: 'Notified At',
            key: 'notifiedCheckIn',
            dataIndex: 'lastCheckInNotificationEvent',
            render: (event, row) => {
                if (!event) return '—';

                if (!event.success){
                    return (
                        <Tooltip title={
                            <Typography.Text type="danger">
                                {`Notification failed: ${event.errorMessage || 'Unknown error'}`}
                            </Typography.Text>
                        }>
                            <Typography.Text type="danger">ERROR <Icon type="down" /></Typography.Text>
                        </Tooltip>
                    )
                }

                const eventMoment = event.createdTs ? moment.tz(event.createdTs, 'America/Chicago') : null;

                if (!eventMoment) return '—';

                return <span style={{ whiteSpace: 'nowrap' }}>{eventMoment.format('YYYY-MM-DD HH:mm') + ' CST'}</span>
            }
        },
        {
            title: 'Created By',
            key: 'createdBy',
            dataIndex: 'createdBy'
        }
    ]

    return columns.filter((column) => {
        if (visibleColumns && !visibleColumns.includes(column.key as FlightEditorEntityColumn)) return false;
        if (!showSafetyCard && column.key === 'safetyCard') return false;
        if (!showNotesForPilot && column.key === 'pilotNotes') return false;
        return true;
    });
}
